import {Component, Input, OnChanges} from '@angular/core';
import {NgForOf, NgIf} from "@angular/common";
import {Player} from "../../model/broadcast/player";
import {Team} from "../../model/broadcast/team";
import {Broadcast} from "../../model/broadcast/broadcast";
import {UtilService} from "../../services/util.service";

type LabelDir = 'below' | 'right' | 'left';

interface FieldSpot {
  position: number;
  positionLabel: string;
  number: string;
  name: string;
  surname: string;
  givenNames: string;
  cx: number;
  cy: number;
  labelX: number;
  labelY: number;
  labelAnchor: 'start' | 'middle' | 'end';
}

export interface BenchEntry {
  number: string;
  name: string;
}

const POSITION_COORDS: Record<number, { cx: number; cy: number; dir: LabelDir }> = {
  1: {cx: 120, cy: 138, dir: 'below'}, // P
  2: {cx: 120, cy: 207, dir: 'below'}, // C
  3: {cx: 168, cy: 130, dir: 'right'}, // 1B
  4: {cx: 152, cy: 95,  dir: 'right'}, // 2B
  5: {cx: 72,  cy: 130, dir: 'left'},  // 3B
  6: {cx: 88,  cy: 95,  dir: 'left'},  // SS
  7: {cx: 48,  cy: 62,  dir: 'below'}, // LF
  8: {cx: 120, cy: 40,  dir: 'below'}, // CF
  9: {cx: 192, cy: 62,  dir: 'below'}, // RF
};

@Component({
  selector: 'app-defense',
  templateUrl: './defense.component.html',
  styleUrls: ['./defense.component.scss'],
  standalone: true,
  imports: [NgIf, NgForOf],
})
export class DefenseComponent implements OnChanges {
  @Input() team: Team;
  @Input() broadcast: Broadcast;
  @Input() teamName?: string;
  @Input() bench: BenchEntry[] | null = null;

  fieldSpots: FieldSpot[] = [];
  benchSpots: FieldSpot[] = [];

  constructor(public utilService: UtilService) {
  }

  ngOnChanges(): void {
    this.rebuild();
  }

  private rebuild(): void {
    this.fieldSpots = [];
    this.benchSpots = [];
    if (!this.team) return;

    const byPosition = new Map<number, Player>();
    this.team.lineup?.forEach(spot => {
      const player = spot.players[spot.players.length - 1];
      if (!player) return;
      if (player.position == null) return;
      byPosition.set(player.position, player);
    });

    if (!byPosition.has(1) && this.team.pitchers?.length) {
      byPosition.set(1, this.team.pitchers[this.team.pitchers.length - 1]);
    }

    byPosition.forEach((player, position) => {
      const coords = POSITION_COORDS[position];
      const split = this.splitName(player.name ?? '');
      const base = {
        position,
        positionLabel: this.utilService.positionToString(position) ?? '',
        number: this.utilService.getNumber(player, this.broadcast),
        name: player.name ?? '',
        surname: split.surname,
        givenNames: split.givenNames,
      };
      if (!coords) {
        this.benchSpots.push({...base, cx: 0, cy: 0, labelX: 0, labelY: 0, labelAnchor: 'start'});
        return;
      }
      const label = this.labelOffset(coords.cx, coords.cy, coords.dir);
      this.fieldSpots.push({...base, cx: coords.cx, cy: coords.cy, ...label});
    });

    this.fieldSpots.sort((a, b) => a.position - b.position);
    this.benchSpots.sort((a, b) => a.position - b.position);
  }

  private labelOffset(cx: number, cy: number, dir: LabelDir): { labelX: number; labelY: number; labelAnchor: 'start' | 'middle' | 'end' } {
    if (dir === 'right') return {labelX: cx + 11, labelY: cy, labelAnchor: 'start'};
    if (dir === 'left') return {labelX: cx - 11, labelY: cy, labelAnchor: 'end'};
    return {labelX: cx, labelY: cy + 16, labelAnchor: 'middle'};
  }

  private splitName(name: string): { surname: string; givenNames: string } {
    if (!name) return {surname: '', givenNames: ''};
    const idx = name.indexOf(' ');
    if (idx < 0) return {surname: name, givenNames: ''};
    return {
      surname: name.substring(0, idx),
      givenNames: name.substring(idx + 1).trim(),
    };
  }
}
